// Client-side slug helpers for the admin Create-Org form's live preview
// and the OrgDetail edit form's validation. Server is authoritative;
// these mirror cloud/admin/workers/src/lib/slugify.js exactly so the
// preview matches what the Worker would persist.
//
// SYNC: also defined in cloud/admin/workers/src/lib/slugify.js,
//       cloud/workers/src/lib/slugify.js, and
//       cloud/dashboard/src/lib/orgSlug.js (operator-facing dashboard).

const SLUG_MIN_LENGTH = 2;
const SLUG_MAX_LENGTH = 32;
const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

const RESERVED_SLUGS = new Set([
  'admin', 'api', 'www', 'app', 'mail', 'ftp',
  'staging', 'dev', 'test', 'status', 'docs',
]);

export function isReservedSlug(slug) {
  return RESERVED_SLUGS.has(slug);
}

/**
 * Derive a slug from a display name (best-effort, lower-cased, hyphen-
 * separated). Used to live-preview a slug as the operator types the
 * org name in the Create form.
 */
export function normalizeSlug(input) {
  if (typeof input !== 'string') return '';
  return input
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Validate a slug client-side. Returns null when valid; otherwise a
 * full-sentence human-readable error string. Strings match the server's
 * validateSlug() byte-for-byte (enforced by slugify-mirror.test.js in
 * the admin Workers package) so the same message is shown whether the
 * failure is caught client-side or returned by the Worker — operators
 * see consistent wording either way. vista#339 review note 2.
 */
export function validateSlug(slug) {
  if (typeof slug !== 'string' || slug.length === 0) {
    return 'slug is required';
  }
  if (slug.length < SLUG_MIN_LENGTH) {
    return `slug must be at least ${SLUG_MIN_LENGTH} characters`;
  }
  if (slug.length > SLUG_MAX_LENGTH) {
    return `slug must be at most ${SLUG_MAX_LENGTH} characters`;
  }
  if (!SLUG_PATTERN.test(slug)) {
    return 'slug must contain only lowercase letters, digits, and hyphens; no leading, trailing, or consecutive hyphens';
  }
  if (RESERVED_SLUGS.has(slug)) {
    return `"${slug}" is a reserved name and cannot be used as an organization slug`;
  }
  return null;
}

export { RESERVED_SLUGS, SLUG_MIN_LENGTH, SLUG_MAX_LENGTH };
